// Default home-page content. Used by the storefront (Home.js) when the admin hasn't
// saved a value yet, and by the Admin "Home" tab to pre-fill its forms, so both
// always read/write the same shape of data (stored in the `home` content section).

export const HOME_DEFAULTS = {
  popup: {
    enabled: false,
    image: '',
    imageMobile: '',
    alt: 'Special website promotion',
    link: '/products',
    delaySeconds: 1,
    frequency: 'session', // always | session | day
  },
  featured: {
    title: 'Featured Products',
    searchPlaceholder: 'Search your perfect ride...',
    tabs: [
      { label: 'Electric Scooters', category: 'electric-scooters' },
      { label: 'Electric Bikes', category: 'electric-bikes' },
    ],
    limit: 8,
    buttonLabel: 'View Product',
    viewAllLabel: 'Explore All Products',
    viewAllLink: '/products',
  },
  afterSales: {
    enabled: true,
    eyebrow: 'Support',
    title: 'Complete After-Sales',
    cards: [
      {
        title: 'Warranty & Customer Care',
        text: 'At Green Wheels Scooters, our service doesn’t end when you make a purchase. We provide 1-year warranty support and are committed to making repairs and after-sales service as straightforward as possible.',
        bullets: ['1-year warranty support', 'Help with warranty and product enquiries', 'Technical assistance from our team'],
      },
      {
        title: 'Repairs & Servicing',
        text: 'Our Dublin service centre provides electric scooter, electric bike and mobility scooter repairs, servicing and spare parts. No appointment is required for repairs — simply bring your vehicle to our service centre and our team will assist you.',
        bullets: ['Electric scooter repairs', 'Electric bike and mobility scooter servicing', 'No appointment required for repairs'],
      },
      {
        title: 'Genuine Parts & Ongoing Support',
        text: 'We also stock and source genuine and compatible spare parts and accessories, helping you keep your electric vehicle running safely and reliably.',
        bullets: ['Genuine and compatible spare parts', 'Accessories for your electric vehicle', 'Support throughout your ownership journey'],
      },
    ],
    hereTitle: 'HERE WHEN YOU NEED US',
    hereParas: [
      'We believe excellent customer service means being available when our customers need support. Our team is always available by phone to answer questions, provide technical assistance and help with warranty, repairs and product enquiries.',
      'From choosing your first electric scooter or e-bike to servicing and spare parts, Green Wheels Scooters is here to support you throughout your ownership journey.',
    ],
    ctaLabel: 'Shop online or visit our Dublin store today',
    ctaLink: '/products',
  },
  blogSection: {
    enabled: true,
    eyebrow: 'IDEAS WORTH SHARING',
    titleLine1: 'Inspiration for',
    titleLine2: 'Every Occasion.',
    subtitle: 'Discover thoughtful ideas, meaningful moments and fresh inspiration.',
    count: 3,
    ctaLabel: 'Explore All Articles',
    ctaLink: '/blog',
  },
  chatbot: {
    enabled: true,
    botName: 'Green Wheels Assistant',
    welcome: 'Hi! 👋 I’m the Green Wheels assistant. Ask me about delivery, warranty, repairs, finance, or any scooter or e-bike — or pick a topic below.',
    teaser: 'Questions about delivery?',
    showTeaser: true,
    whatsapp: '353871002241',
    // Customer support: when the bot can't help (or the customer asks for a person), they can send a
    // message to the team. It goes through the same /api/contact endpoint as the Contact Us page.
    supportEnabled: true,
    supportIntro: 'I’ll pass this to our team. Leave your details and they’ll get back to you by email or phone.',
    supportSuccess: 'Thanks {name}! Your message has been sent to our team — we’ll get back to you as soon as possible.',
    fallback: 'Sorry, I’m not sure about that one. You can call us on {phone}, email {email}, or chat to the team on WhatsApp — we’re happy to help.',
    quickReplies: ['Delivery info', 'Warranty', 'Repairs', 'Pay in 3 with Klarna', 'Opening hours', 'Electric scooters', 'Electric bikes'],
    faqs: [
      { title: 'Delivery', keywords: 'delivery, deliver, shipping, ship, postage, arrive, dispatch, courier, how long', answer: 'We deliver countrywide across Ireland. The delivery cost and an estimated delivery date are shown on every product page and again at checkout.', linkLabel: 'Browse products', link: '/products' },
      { title: 'Warranty', keywords: 'warranty, guarantee, covered, faulty, defect, fault', answer: 'Our electric scooters and e-bikes come with 1-year warranty support, and our team can help with warranty and technical questions.', linkLabel: 'Warranty policy', link: '/support/warranty', handoff: true },
      { title: 'Repairs', keywords: 'repair, repairs, service, servicing, broken, puncture, tyre, tire, brake, fix, maintenance, tune up, mechanic', answer: 'Our Dublin service centre repairs and services electric scooters, e-bikes and mobility scooters. No appointment is needed for repairs — just bring it in. You can also book online.', linkLabel: 'Book a repair', link: '/repair-booking' },
      { title: 'Klarna', keywords: 'klarna, finance, instalment, instalments, installment, pay later, emi, monthly, split, in 3, three payments, pay in 3', answer: 'You can spread the cost in 3 interest-free payments with Klarna — just choose Klarna at checkout. Subject to Klarna’s terms and eligibility.', linkLabel: 'Shop now', link: '/products' },
      { title: 'Bike to Work', keywords: 'bike to work, cycle to work, salary sacrifice, tax saving, scheme', answer: 'Yes — you can buy an e-bike through the Bike to Work scheme and save on tax. Here is how it works.', linkLabel: 'Bike to Work scheme', link: '/bike-to-work' },
      { title: 'Opening hours & location', keywords: 'hours, open, opening, close, closing, location, address, where are you, visit, directions, find you, store, shop, showroom', answer: 'You can visit us at {address}. We’re open {hours}.', linkLabel: 'Get directions', link: '/contact' },
      { title: 'Contact', keywords: 'contact, phone, call, email, speak, human, agent, person, talk, number', answer: 'You can call us on {phone} or email {email}. We’re open {hours}.', linkLabel: 'Contact page', link: '/contact', handoff: true },
      { title: 'Returns & refunds', keywords: 'return, returns, refund, refunds, exchange, cancel, money back, send back', answer: 'You can read our full return and refund terms on the policy page, or call us on {phone} and we’ll help.', linkLabel: 'Return & refund policy', link: '/support/return-refund', handoff: true },
      { title: 'Payment', keywords: 'pay, payment, payments, card, paypal, apple pay, google pay, revolut, secure checkout', answer: 'At checkout you can pay by card or a digital wallet, and spread the cost with Klarna on eligible orders. Checkout is secure.', linkLabel: 'View cart', link: '/cart' },
      { title: 'Orders', keywords: 'my order, orders, track, tracking, order status, where is my', answer: 'Sign in and open “My Orders” to see the status of your order. If something looks wrong, call us on {phone}.', linkLabel: 'My orders', link: '/orders', handoff: true },
      { title: 'Rentals', keywords: 'rent, rental, rentals, hire, hiring', answer: 'We also offer rentals — you can check availability and book online.', linkLabel: 'Book a rental', link: '/rental-booking' },
    ],
  },
  // The button under the home-page Google reviews strip opens this page (your Google reviews).
  googleReviews: {
    // Google's reviews page for the shop, built from its Google place ID (a name-based maps link does not resolve)
    url: 'https://search.google.com/local/reviews?placeid=ChIJg3DaXgANZ0gRSZS-gldyn3w',
    label: 'See all reviews',
  },
  serviceEnabled: true,
  serviceBadges: [
    { icon: '🚚', text: 'Deliveries Countrywide' },
    { icon: '📜', text: '1 Year Warranty' },
    { icon: '📞', text: 'Online Support' },
  ],
  serviceImage: '',
  serviceCta: '',
  serviceCtaLink: '',
};

// Shallow-per-section merge: admin value wins, defaults fill missing keys/sections.
export function withHomeDefaults(data) {
  const d = data || {};
  const out = { ...d };
  ['popup', 'featured', 'afterSales', 'blogSection', 'chatbot', 'googleReviews'].forEach(k => {
    out[k] = { ...HOME_DEFAULTS[k], ...(d[k] || {}) };
  });
  if (!out.featured.tabs?.length) out.featured.tabs = HOME_DEFAULTS.featured.tabs;
  // Event popups: a scheduled list. Sites that only have the old single popup get it carried over
  // as the first list entry (no dates = always live while enabled), so nothing is lost on upgrade.
  if (!Array.isArray(d.popups)) {
    out.popups = d.popup?.image
      ? [{ id: 'legacy', name: 'Home popup', startsAt: '', endsAt: '', ...HOME_DEFAULTS.popup, ...d.popup }]
      : [];
  }
  if (!Array.isArray(d.chatbot?.faqs)) out.chatbot.faqs = HOME_DEFAULTS.chatbot.faqs;
  if (!Array.isArray(d.chatbot?.quickReplies)) out.chatbot.quickReplies = HOME_DEFAULTS.chatbot.quickReplies;
  if (d.serviceEnabled === undefined) out.serviceEnabled = HOME_DEFAULTS.serviceEnabled;
  if (!Array.isArray(d.serviceBadges)) out.serviceBadges = HOME_DEFAULTS.serviceBadges;
  return out;
}
